import { memo } from "react"

import { useHelpOptional } from "@/components/help/help-provider"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"

/**
 * The assistant answers in markdown — the system prompt asks it to lead with the
 * answer and it reaches for bold, bullets and tables on its own. Rendering the
 * raw string printed the asterisks (INCO-294).
 *
 * react-markdown builds a React tree rather than setting innerHTML, so model
 * output can't inject markup no matter what a counterparty's message contained.
 * Raw HTML in the source is passed through as text for the same reason — there
 * is deliberately no rehype-raw here.
 *
 * It also has to survive being called on every chunk while a reply streams in,
 * so it sees half-written markdown constantly: an unclosed `**`, a table with
 * one row of its header. A real parser degrades to plain text on those rather
 * than throwing, which is most of why this isn't hand-rolled.
 */

// Tailwind's prose plugin assumes an article. These pull it back to a chat
// bubble: no top/bottom margin at the edges, tighter list spacing, and colour
// taken from the design tokens rather than prose's own greys.
const PROSE = [
  "prose prose-sm max-w-none",
  "prose-p:my-2 first:prose-p:mt-0 last:prose-p:mb-0",
  "prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5",
  "prose-headings:font-medium prose-headings:text-foreground prose-headings:mt-3 prose-headings:mb-1.5",
  "prose-strong:font-semibold prose-strong:text-foreground",
  "prose-hr:my-3 prose-hr:border-border",
  "prose-a:text-foreground prose-a:underline-offset-2",
  "text-foreground prose-p:text-foreground prose-li:text-foreground",
].join(" ")

function AssistantMarkdownImpl({ content }: { content: string }) {
  const help = useHelpOptional()

  return (
    <div className={PROSE}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Two kinds of link, and the difference matters.
          //
          // `/help/<slug>` is one the model was *told* to emit, by the
          // `search_help` prompt rule, and the slug came back from our own
          // corpus rather than out of the model. It opens the article beside
          // the conversation (INCO-394) — sending somebody to a new tab to
          // read two paragraphs is what makes an assistant answer feel like a
          // dead end.
          //
          // Everything else is model output pointing anywhere at all: new tab,
          // and it never gets to name this window as its opener.
          a: ({ href, children, node: _node, ...props }) => {
            const article = href?.match(/^\/help\/([a-z0-9-]+)$/)

            if (article && help) {
              return (
                <button
                  type="button"
                  onClick={() => help.open(article[1])}
                  className="text-foreground underline underline-offset-2"
                >
                  {children}
                </button>
              )
            }

            return (
              <a href={href} {...props} target="_blank" rel="noopener noreferrer nofollow">
                {children}
              </a>
            )
          },
          // Numbers are the whole point of a table here (recipients, volumes,
          // scores), so it gets the mono treatment DESIGN.md reserves for data
          // and its own scroll container rather than widening the panel.
          table: ({ ...props }) => (
            <div className="my-2 overflow-x-auto">
              <table
                {...props}
                className="w-full border-collapse text-xs"
              />
            </div>
          ),
          th: ({ ...props }) => (
            <th
              {...props}
              className="border-b border-border px-2 py-1 text-left text-xs font-semibold text-muted-foreground"
            />
          ),
          td: ({ ...props }) => (
            <td {...props} className="border-b border-border px-2 py-1 align-top" />
          ),
          code: ({ ...props }) => (
            <code
              {...props}
              className="rounded bg-background/60 px-1 py-0.5 text-xs before:content-none after:content-none"
            />
          ),
          pre: ({ ...props }) => (
            <pre
              {...props}
              className="my-2 overflow-x-auto rounded bg-background/60 p-2 text-xs"
            />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}

// Streaming re-renders the whole message list on every chunk; only the message
// whose text actually grew needs to re-parse.
export const AssistantMarkdown = memo(AssistantMarkdownImpl)
